import React from "react";
import { useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Minus, Plus, Trash2, ShoppingBag, Truck } from "lucide-react";
import { useCart } from "../context/CartContext";
import { restaurant } from "../mock/menuData";

const fmt = (n) => n.toFixed(2).replace(".", ",") + "€";

const LineConfig = ({ line }) => {
  const bits = [];
  bits.push(line.format === "menu" ? "Menu" : "Seul");
  if (line.meats?.length) bits.push(line.meats.join(", "));
  if (line.support) bits.push(line.support);
  if (line.crudites?.length) bits.push("Crudités: " + line.crudites.join(", "));
  if (line.sauces?.length) bits.push("Sauce: " + line.sauces.join(", "));
  if (line.boisson) bits.push(line.boisson);
  return <p className="text-white/45 text-xs mt-0.5">{bits.join(" · ")}</p>;
};

const CartDrawer = () => {
  const { items, open, setOpen, updateQty, removeItem, subtotal, count } = useCart();
  const navigate = useNavigate();
  const remaining = Math.max(0, restaurant.freeDeliveryFrom - subtotal);
  const pct = Math.min(100, (subtotal / restaurant.freeDeliveryFrom) * 100);

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent className="bg-[#0f0f0f] border-l border-white/10 text-white w-full sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="p-5 border-b border-white/10">
          <SheetTitle className="text-white font-display text-2xl uppercase flex items-center gap-2">
            <ShoppingBag size={22} className="text-pink" /> Mon Panier ({count})
          </SheetTitle>
        </SheetHeader>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-center px-6">
            <ShoppingBag size={54} className="text-white/20 mb-4" />
            <p className="text-white/60">Votre panier est vide.</p>
            <button onClick={() => { setOpen(false); navigate("/carte"); }} className="mt-5 bg-pink text-white font-display uppercase px-6 py-2.5 rounded-full btn-shine">
              Voir la carte
            </button>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {items.map((line) => (
                <div key={line.lineId} className="bg-[#181818] rounded-xl p-3 flex gap-3 border border-white/5">
                  <img src={line.image} alt={line.name} className="w-16 h-16 rounded-lg object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between gap-2">
                      <h4 className="font-semibold text-sm">{line.name}</h4>
                      <button onClick={() => removeItem(line.lineId)} className="text-white/40 hover:text-pink"><Trash2 size={16} /></button>
                    </div>
                    <LineConfig line={line} />
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center gap-2 bg-white/10 rounded-full px-1.5 py-0.5">
                        <button onClick={() => updateQty(line.lineId, line.qty - 1)} className="p-1 hover:text-pink"><Minus size={14} /></button>
                        <span className="text-sm font-bold w-4 text-center">{line.qty}</span>
                        <button onClick={() => updateQty(line.lineId, line.qty + 1)} className="p-1 hover:text-pink"><Plus size={14} /></button>
                      </div>
                      <span className="text-gold font-bold text-sm">{fmt(line.unitPrice * line.qty)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-4 border-t border-white/10 space-y-4 bg-[#111]">
              <div>
                <div className="flex items-center gap-2 text-xs text-white/60 mb-1.5">
                  <Truck size={14} className="text-gold" />
                  {remaining > 0 ? <span>Plus que <b className="text-gold">{fmt(remaining)}</b> pour la livraison gratuite</span> : <span className="text-gold font-semibold">Livraison gratuite débloquée 🎉</span>}
                </div>
                <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                  <div className="h-full bg-gold transition-all" style={{ width: `${pct}%` }} />
                </div>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-white/70">Sous-total</span>
                <span className="font-display text-2xl text-white">{fmt(subtotal)}</span>
              </div>
              <button
                onClick={() => { setOpen(false); navigate("/checkout"); }}
                className="w-full bg-pink text-white font-display uppercase text-lg py-3 rounded-full btn-shine"
              >
                Passer commande
              </button>
              <button
                onClick={() => { setOpen(false); navigate("/carte"); }}
                className="w-full bg-transparent border-2 border-white/25 text-white font-display uppercase text-sm py-2.5 rounded-full hover:border-gold hover:text-gold transition-colors"
              >
                Continuer mes achats
              </button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};

export default CartDrawer;
